import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FieldErrorDirective } from '@app-shared';
import { GlobalConstants } from 'app/core/global-constants';
import { ClaimService } from 'app/core/services/claim.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-submission',
    imports: [ReactiveFormsModule, FieldErrorDirective],
    templateUrl: './submission.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './submission.component.scss',
})
export class SubmissionComponent implements OnInit, OnDestroy {
    private readonly claimService = inject(ClaimService);
    private readonly router = inject(Router);
    private readonly formBuilder = inject(FormBuilder);
    private claimSubscription: Subscription;
    claimTypes = GlobalConstants.CLAIM_TYPE;
    claimStatus = GlobalConstants.CLAIM_STATUS;
    submissionForm: FormGroup;
    isSaving = false;

    get submissionFormControls() {
        return this.submissionForm.controls;
    }

    ngOnInit(): void {
        this.initializeClaimForm();
    }

    initializeClaimForm() {
        this.submissionForm = this.formBuilder.group({
            claimReferenceNumber: ['', Validators.required],
            studentLastName: ['', Validators.required],
        });
    }

    onSubmit() {
        this.isSaving = true;
        this.markFormGroupTouched(this.submissionForm);
        if (this.submissionForm.valid) {
            this.claimSubscription = this.claimService
                .getClaimByReferenceAndStudentLastName(this.submissionForm.value.claimReferenceNumber, this.submissionForm.value.studentLastName)
                .subscribe({
                    next: (response) => {
                        if (response && response.claimStatusId === this.claimStatus.PENDING_PHYSICIAN.id) {
                            this.router.navigate(['/physician-response'], { queryParams: { guid: response.guid } });
                        } else if (response && response.claimStatusId === this.claimStatus.PENDING_EMPLOYER.id) {
                            this.router.navigate(['/employer-response'], { queryParams: { guid: response.guid } });
                        } else {
                            this.router.navigate(['/not-actionable'], { skipLocationChange: true });
                        }
                    },
                });
        } else {
            this.isSaving = false;
        }
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.claimSubscription) {
            this.claimSubscription.unsubscribe();
        }
    }
}
